// The outbox — the durable queue of not-yet-synced mutations, and the seams
// the capture form (and, later, edit/stage controls) call into.
//
// ─────────────────────────────────────────────────────────────────────────
// SEAMS FOR LATER WAVES
// ─────────────────────────────────────────────────────────────────────────
//   • enqueueLeadCreate(...)  — the capture form calls THIS on submit. It
//     writes the outbox row, the card bytes (as ArrayBuffer) and an optimistic
//     leadsDraft row in ONE transaction, then nudges the sync engine. Works
//     identically online and offline; online just drains a moment later.
//   • enqueueLeadUpdate / enqueueLeadStageChange — ONLINE-ONLY producers. The
//     edit form / stage chips must gate their controls on `useIsOnline()`
//     (see use-online.ts) and only call these with connectivity. They exist
//     here so a mutation enqueued on a flaky-but-present link still drains.
//   • retryOutboxEntry / discardOutboxEntry — the /sync recovery screen.
//   • listOutbox — the /sync screen and the board's pending-count read it.
//
// countUnsyncedWork() is registered into lib/db.ts so the logout guard in
// auth/session.ts can refuse to wipe a booth's worth of undelivered leads.
import type { CreateLeadInput, StageChangeInput, UpdateLeadInput } from '@paradise/shared';

import {
  db,
  registerUnsyncedWorkCounter,
  type CardBlobRow,
  type LeadCreatePayload,
  type LeadStageChangePayload,
  type LeadUpdatePayload,
  type OutboxRow
} from '@/lib/db';
import { newUuid } from '@/lib/uuid';

import { deriveBadgeKind, useSyncStatus } from './sync-status-store';

/** Bytes captured from the camera/file input, ready to ride the multipart request. */
export interface CardCapture {
  bytes: ArrayBuffer;
  contentType: string;
}

// The sync engine registers its drain trigger here to avoid an import cycle
// (the engine imports this module). Until it does, enqueuing simply persists —
// the next boot's initial drain pass picks the work up regardless.
let drainTrigger: (() => void) | null = null;

export function registerDrainTrigger(fn: () => void): void {
  drainTrigger = fn;
}

function nudgeDrain(): void {
  drainTrigger?.();
}

// ── Enqueue ────────────────────────────────────────────────────────────────

function newOutboxRow(
  clientUuid: string,
  kind: OutboxRow['kind'],
  payload: OutboxRow['payload'],
  company: string,
  hasCard: boolean,
  hasCardBack: boolean
): OutboxRow {
  return {
    clientUuid,
    kind,
    payload,
    status: 'pending',
    hasCard,
    hasCardBack,
    company,
    createdAt: Date.now(),
    attempts: 0,
    serverAttempts: 0,
    lastAttemptAt: null,
    lastErrorCode: null,
    lastError: null
  };
}

function cardBlobRow(clientUuid: string, card: CardCapture): CardBlobRow {
  return {
    clientUuid,
    bytes: card.bytes,
    contentType: card.contentType,
    sizeBytes: card.bytes.byteLength,
    createdAt: Date.now()
  };
}

/**
 * Capture-form seam. `input.clientUuid` MUST be a uuid v4 generated before
 * submit — it is the idempotency key the drain replays against, so a request
 * that actually succeeded replays as a safe no-op, never a duplicate.
 */
export async function enqueueLeadCreate(
  input: CreateLeadInput,
  cardFront?: CardCapture,
  cardBack?: CardCapture
): Promise<void> {
  const payload: LeadCreatePayload = { input };
  await db.transaction('rw', db.outbox, db.cardBlobs, db.cardBackBlobs, db.leadsDraft, async () => {
    await db.outbox.put(
      newOutboxRow(
        input.clientUuid,
        'lead.create',
        payload,
        input.company,
        cardFront !== undefined,
        cardBack !== undefined
      )
    );
    if (cardFront) await db.cardBlobs.put(cardBlobRow(input.clientUuid, cardFront));
    if (cardBack) await db.cardBackBlobs.put(cardBlobRow(input.clientUuid, cardBack));
    await db.leadsDraft.put({
      clientUuid: input.clientUuid,
      status: 'pending_sync',
      ref: null,
      serverId: null,
      input,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
  });
  await refreshSyncCounts();
  nudgeDrain();
}

/** Online-only producer (see file header). `clientUuid` is a fresh op id, not the lead's. */
export async function enqueueLeadUpdate(
  clientUuid: string,
  leadId: string,
  company: string,
  input: UpdateLeadInput,
  cardFront?: CardCapture,
  cardBack?: CardCapture
): Promise<void> {
  const payload: LeadUpdatePayload = { leadId, input };
  await db.transaction('rw', db.outbox, db.cardBlobs, db.cardBackBlobs, async () => {
    await db.outbox.put(
      newOutboxRow(clientUuid, 'lead.update', payload, company, cardFront !== undefined, cardBack !== undefined)
    );
    if (cardFront) await db.cardBlobs.put(cardBlobRow(clientUuid, cardFront));
    if (cardBack) await db.cardBackBlobs.put(cardBlobRow(clientUuid, cardBack));
  });
  await refreshSyncCounts();
  nudgeDrain();
}

/** Online-only producer (see file header). */
export async function enqueueLeadStageChange(
  clientUuid: string,
  leadId: string,
  company: string,
  input: StageChangeInput
): Promise<void> {
  const payload: LeadStageChangePayload = { leadId, input };
  await db.outbox.put(newOutboxRow(clientUuid, 'lead.stageChange', payload, company, false, false));
  await refreshSyncCounts();
  nudgeDrain();
}

// ── Amending a queued create ─────────────────────────────────────────────────

/** Card sides as an edit expresses them: new bytes, an explicit removal, or neither (leave alone). */
export interface CardAmendment {
  front?: CardCapture;
  back?: CardCapture;
  frontRemoved?: boolean;
  backRemoved?: boolean;
}

/**
 * Edit a lead that has not reached the server yet, by rewriting its queued
 * `lead.create` in place — same `clientUuid`, same single request, just an
 * amended payload.
 *
 * This is not a shortcut around `lead.update`. The server's replay branch
 * (apps/api `routes/leads.ts`, `insertLead`) returns the EXISTING lead for a
 * known `clientUuid` and never applies the incoming payload, so a second entry
 * under a fresh uuid would duplicate the lead and one under the same uuid would
 * be ignored. Amending is the only shape that is correct offline.
 *
 * Works with no connection, unlike editing a synced lead — there is no server
 * round trip to gate on. If the entry has already been delivered by the time
 * this runs (it drained while the form was open), the edit is preserved as a
 * queued `lead.update` against the now-known server id instead of being lost.
 */
export async function amendLeadCreate(
  clientUuid: string,
  input: CreateLeadInput,
  cards: CardAmendment = {}
): Promise<void> {
  await db.transaction('rw', db.outbox, db.cardBlobs, db.cardBackBlobs, db.leadsDraft, db.leads, async () => {
    const row = await db.outbox.get(clientUuid);
    const draft = await db.leadsDraft.get(clientUuid);

    if (!row || row.kind !== 'lead.create' || row.status === 'done') {
      // Delivered while the form was open. The create cannot carry the edit any
      // more, so express it as an update against the id the drain wrote back.
      if (draft?.serverId) {
        await queueUpdateForSyncedLead(draft.serverId, clientUuid, input, cards);
        await db.leadsDraft.update(clientUuid, { input, updatedAt: Date.now() });
      }
      return;
    }

    await applyCardAmendment(clientUuid, cards);
    const hasCard = (await db.cardBlobs.get(clientUuid)) !== undefined;
    const hasCardBack = (await db.cardBackBlobs.get(clientUuid)) !== undefined;

    const payload: LeadCreatePayload = { input };
    await db.outbox.update(clientUuid, {
      payload,
      company: input.company,
      hasCard,
      hasCardBack,
      amendedAt: Date.now(),
      // An edit is a human saying "send this one" — the same intent Retry
      // carries, so it clears the error state and the backoff the same way. An
      // entry mid-flight keeps its status: settleResult owns that row's outcome
      // and the reconcile check covers the payload it raced.
      ...(row.status === 'failed_transient' || row.status === 'failed_permanent'
        ? { status: 'pending' as const, attempts: 0, serverAttempts: 0, lastAttemptAt: null }
        : {}),
      lastErrorCode: null,
      lastError: null
    });

    await db.leadsDraft.update(clientUuid, {
      input,
      updatedAt: Date.now(),
      // A parked draft that has just been edited is back in the ordinary queue,
      // so it must stop rendering "Needs attention" on the board.
      ...(draft?.status === 'failed' ? { status: 'pending_sync' as const } : {})
    });
  });
  await refreshSyncCounts();
  nudgeDrain();
}

/** New bytes replace a side; an explicit removal deletes it; neither leaves it alone. */
async function applyCardAmendment(clientUuid: string, cards: CardAmendment): Promise<void> {
  if (cards.front) await db.cardBlobs.put(cardBlobRow(clientUuid, cards.front));
  else if (cards.frontRemoved) await db.cardBlobs.delete(clientUuid);

  if (cards.back) await db.cardBackBlobs.put(cardBlobRow(clientUuid, cards.back));
  else if (cards.backRemoved) await db.cardBackBlobs.delete(clientUuid);
}

/**
 * Express a `CreateLeadInput` as a queued `lead.update` against a lead the
 * server already holds — the reconcile path for an amendment that lost the race
 * with its own create (here, and from the drain in sync-engine.ts).
 *
 * Card bytes are COPIED to the new entry's uuid rather than moved: the create
 * that won the race deletes the originals on success, and a side the amendment
 * removed has to travel as an explicit null key or the server keeps the old
 * photo forever.
 *
 * Runs inside the caller's Dexie transaction (`outbox`, both card tables and
 * `leads` must be in its scope).
 */
export async function queueUpdateForSyncedLead(
  serverId: string,
  sourceUuid: string,
  input: CreateLeadInput,
  cards: CardAmendment = {}
): Promise<void> {
  await applyCardAmendment(sourceUuid, cards);
  const front = await db.cardBlobs.get(sourceUuid);
  const back = await db.cardBackBlobs.get(sourceUuid);

  const patch: UpdateLeadInput = {
    items: input.items,
    company: input.company,
    contactName: input.contactName ?? null,
    phone: input.phone ?? null,
    email: input.email ?? null,
    country: input.country ?? null,
    advance: input.advance ?? null,
    paymentMode: input.paymentMode ?? null,
    followUpDate: input.followUpDate ?? null,
    notes: input.notes ?? null,
    // A side with no bytes to send must be cleared explicitly — omitting the
    // key means "leave it alone", which would strand a photo the user deleted.
    ...(front ? {} : { cardPhotoKey: null, cardFrontOriginalKey: null }),
    ...(back ? {} : { cardBackKey: null, cardBackOriginalKey: null })
  };

  const updateUuid = newUuid();
  await db.outbox.put(
    newOutboxRow(updateUuid, 'lead.update', { leadId: serverId, input: patch }, input.company, !!front, !!back)
  );
  if (front) await db.cardBlobs.put({ ...front, clientUuid: updateUuid });
  if (back) await db.cardBackBlobs.put({ ...back, clientUuid: updateUuid });

  // `stage` cannot ride a PATCH (see UpdateLeadInput's doc comment) and a
  // stage move on an existing lead writes a history row, so it is only queued
  // when the mirror says it would actually change something — a no-op move
  // would leave a phantom entry on the buyer's timeline.
  const mirrored = await db.leads.get(serverId);
  if (input.stage && mirrored && mirrored.stage !== input.stage) {
    await db.outbox.put(
      newOutboxRow(
        newUuid(),
        'lead.stageChange',
        { leadId: serverId, input: { stage: input.stage } },
        input.company,
        false,
        false
      )
    );
  }
}

// ── Recovery-screen operations ───────────────────────────────────────────────

/** Everything not yet delivered, newest attempt-order preserved (FIFO by createdAt). */
export async function listOutbox(): Promise<OutboxRow[]> {
  const rows = await db.outbox.where('status').notEqual('done').sortBy('createdAt');
  return rows;
}

/** One entry by id — the capture form reads this to prefill an "Edit and retry". */
export async function getOutboxEntry(clientUuid: string): Promise<OutboxRow | undefined> {
  return db.outbox.get(clientUuid);
}

// ── "Edit and retry" hand-off ────────────────────────────────────────────────
// The /sync screen sets a resume target and navigates to the capture form; the
// capture wave reads it on mount (takeResumeTarget), prefills from the outbox
// payload, and on resubmit discards the old entry. sessionStorage so it
// survives the navigation (and a reload) but not a new tab.

const RESUME_KEY = 'paradise:resume-outbox';

export function setResumeTarget(clientUuid: string): void {
  try {
    sessionStorage.setItem(RESUME_KEY, clientUuid);
  } catch {
    // sessionStorage unavailable (rare) — Edit-and-retry degrades to a blank form.
  }
}

/** Reads AND clears the resume target. Capture form calls this once on mount. */
export function takeResumeTarget(): string | null {
  try {
    const value = sessionStorage.getItem(RESUME_KEY);
    if (value) sessionStorage.removeItem(RESUME_KEY);
    return value;
  } catch {
    return null;
  }
}

/** Flip a stuck entry back to `pending` for the next drain. Clears its error + attempt budget. */
export async function retryOutboxEntry(clientUuid: string): Promise<void> {
  await db.outbox.update(clientUuid, {
    status: 'pending',
    attempts: 0,
    // Both counters, or a human's Retry on a parked entry re-parks on its very
    // first failure. `serverAttempts` is the one that spends the budget.
    serverAttempts: 0,
    lastAttemptAt: null,
    lastErrorCode: null,
    lastError: null
  });
  await refreshSyncCounts();
  nudgeDrain();
}

/**
 * Destroy an entry and every trace of it — the outbox row, its card bytes and
 * its optimistic draft — in one transaction. The ONLY place in this app where
 * data genuinely goes away, so the caller must confirm first, naming the
 * company (the /sync screen does).
 */
export async function discardOutboxEntry(clientUuid: string): Promise<void> {
  await db.transaction('rw', db.outbox, db.cardBlobs, db.cardBackBlobs, db.leadsDraft, async () => {
    await db.outbox.delete(clientUuid);
    await db.cardBlobs.delete(clientUuid);
    await db.cardBackBlobs.delete(clientUuid);
    await db.leadsDraft.delete(clientUuid);
  });
  await refreshSyncCounts();
}

// ── Counts ───────────────────────────────────────────────────────────────────

/**
 * "Still trying" vs "permanently stuck", pushed into the badge store. The two
 * are kept separate because a permanent failure drives a warning that must not
 * self-clear (docs/plan.md).
 */
export async function refreshSyncCounts(): Promise<void> {
  const queueLen = await db.outbox.where('status').anyOf(['pending', 'in_flight', 'failed_transient']).count();
  const failedPermanentCount = await db.outbox.where('status').equals('failed_permanent').count();
  useSyncStatus.getState().setCounts({ queueLen, failedPermanentCount });
}

/**
 * Everything that has NOT reached the server — including `failed_permanent`,
 * which is the most important not to silently discard. This is what the logout
 * guard weighs before wiping the device.
 */
export async function countUnsyncedWork(): Promise<number> {
  return db.outbox.where('status').notEqual('done').count();
}

/** Wire the logout guard's counter to the real outbox. Called once from startOffline(). */
export function installUnsyncedWorkCounter(): void {
  registerUnsyncedWorkCounter(countUnsyncedWork);
}

/** Convenience for tests/UI that want the current derived badge face synchronously. */
export function currentBadgeKind() {
  const s = useSyncStatus.getState();
  return deriveBadgeKind(s);
}
